"use client";

import { usePlatformAuthBootstrap } from "@/components/providers/PlatformAuthProvider";
import { useCurrentUser, type CurrentUser } from "@/hooks/queries/useCurrentUser";

export type PlatformAuthState = {
  user: CurrentUser | undefined;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  isResolved: boolean;
};

/**
 * Client auth signal for marketing chrome, CTAs, and footer policy.
 * When wrapped in `PlatformAuthProvider`, first paint follows the server session
 * (no public-navbar flash while `/api/auth/me` loads).
 */
export function usePlatformAuth(): PlatformAuthState {
  const bootstrap = usePlatformAuthBootstrap();
  const initialAuthenticated = bootstrap?.initialAuthenticated;

  const { data: user, isLoading, isError, isSuccess, isFetching } =
    useCurrentUser();

  const isResolved =
    isSuccess || isError || initialAuthenticated !== undefined;

  const isAuthenticated = isSuccess
    ? !!user
    : isError
      ? false
      : (initialAuthenticated ?? false);

  return {
    user: isAuthenticated ? user : undefined,
    isAuthenticated,
    isGuest: isResolved && !isAuthenticated,
    isLoading: !isResolved && (isLoading || isFetching),
    isResolved,
  };
}
